// Ruta: packages/shared/src/telemetry/telemetry.service.ts
// Self-hosted, lightweight telemetry service for HeyShaker frontend apps.
//
// Features:
//   - Error tracking (unhandled errors + promise rejections)
//   - Performance monitoring (page load, route changes)
//   - User action tracking (login, store switch, order view, etc.)
//   - API call tracking (request duration, failures)
//   - Batched event delivery (every 30s or 10 events)
//   - Non-blocking (sendBeacon with fetch fallback)
//
// HAL-FE-002

// ─── Types ───────────────────────────────────────────────────────────────────

export type TelemetryEventType =
  | 'error'
  | 'unhandled_error'
  | 'unhandled_rejection'
  | 'performance'
  | 'user_action'
  | 'api_call'

export interface TelemetryEvent {
  type: TelemetryEventType
  timestamp: string
  sessionId: string
  route: string
  userAgent: string
  payload: Record<string, unknown>
}

export interface TelemetryConfig {
  /** Enable or disable telemetry collection */
  enabled: boolean
  /** BFF endpoint to receive telemetry batches */
  endpoint: string
  /** Maximum events to queue before forcing a flush (default 10) */
  batchSize: number
  /** Flush interval in milliseconds (default 30000) */
  flushIntervalMs: number
  /** Application identifier (e.g. "dashboard", "cms") */
  appName: string
  /** Enable console logging of events in development */
  debug: boolean
}

const DEFAULT_CONFIG: TelemetryConfig = {
  enabled: true,
  endpoint: '/api/v1/telemetry',
  batchSize: 10,
  flushIntervalMs: 30_000,
  appName: 'unknown',
  debug: false,
}

// ─── Session ID ──────────────────────────────────────────────────────────────

function generateSessionId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`
}

// ─── Singleton service ───────────────────────────────────────────────────────

class TelemetryService {
  private _config: TelemetryConfig
  private _queue: TelemetryEvent[] = []
  private _sessionId: string
  private _flushTimer: ReturnType<typeof setInterval> | null = null
  private _initialized = false

  constructor() {
    this._config = { ...DEFAULT_CONFIG }
    this._sessionId = generateSessionId()
  }

  // ─── Initialization ──────────────────────────────────────────────────────

  /**
   * Initialize the telemetry service with configuration.
   * Must be called once at app startup (e.g. in main.tsx).
   * Safe to call multiple times — subsequent calls are no-ops.
   */
  init(config: Partial<TelemetryConfig>): void {
    if (this._initialized) return

    this._config = { ...DEFAULT_CONFIG, ...config }

    if (!this._config.enabled) {
      this._initialized = true
      return
    }

    // Start flush timer
    this._flushTimer = setInterval(() => {
      void this._flush()
    }, this._config.flushIntervalMs)

    // Global error handlers
    if (typeof window !== 'undefined') {
      window.addEventListener('error', this._onGlobalError)
      window.addEventListener('unhandledrejection', this._onUnhandledRejection)

      // Flush on page unload
      window.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          void this._flush()
        }
      })

      // Track initial page load performance
      if (window.performance) {
        // Wait for load event to have complete timing data
        if (document.readyState === 'complete') {
          this._trackPageLoad()
        } else {
          window.addEventListener('load', () => this._trackPageLoad())
        }
      }
    }

    this._initialized = true

    if (this._config.debug) {
    }
  }

  // ─── Public tracking methods ─────────────────────────────────────────────

  /**
   * Track a caught error (e.g. from ErrorBoundary).
   */
  trackError(error: Error, context?: Record<string, unknown>): void {
    this._enqueue('error', {
      name: error.name,
      message: error.message,
      stack: error.stack?.slice(0, 2000),
      ...context,
    })
  }

  /**
   * Track a user action (login, store switch, order view, etc.)
   */
  trackAction(action: string, details?: Record<string, unknown>): void {
    this._enqueue('user_action', {
      action,
      ...details,
    })
  }

  /**
   * Track a route change with optional timing.
   */
  trackRouteChange(from: string, to: string, durationMs?: number): void {
    this._enqueue('performance', {
      metric: 'route_change',
      from,
      to,
      durationMs,
    })
  }

  /**
   * Track an API call with its outcome.
   */
  trackApiCall(params: {
    method: string
    url: string
    status: number
    durationMs: number
    ok: boolean
    errorCode?: string
  }): void {
    this._enqueue('api_call', params)
  }

  /**
   * Track a custom performance metric.
   */
  trackPerformance(metric: string, value: number, unit = 'ms'): void {
    this._enqueue('performance', { metric, value, unit })
  }

  /**
   * Manually flush the event queue (e.g. before navigation).
   */
  flush(): void {
    void this._flush()
  }

  /**
   * Destroy the service — stop timers and remove event listeners.
   */
  destroy(): void {
    if (this._flushTimer !== null) {
      clearInterval(this._flushTimer)
      this._flushTimer = null
    }

    if (typeof window !== 'undefined') {
      window.removeEventListener('error', this._onGlobalError)
      window.removeEventListener('unhandledrejection', this._onUnhandledRejection)
    }

    // Final flush
    void this._flush()
    this._initialized = false
  }

  // ─── Private helpers ─────────────────────────────────────────────────────

  private _enqueue(type: TelemetryEventType, payload: Record<string, unknown>): void {
    if (!this._config.enabled) return

    const event: TelemetryEvent = {
      type,
      timestamp: new Date().toISOString(),
      sessionId: this._sessionId,
      route: typeof window !== 'undefined' ? window.location.pathname : '',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      payload: {
        appName: this._config.appName,
        ...payload,
      },
    }

    this._queue.push(event)

    if (this._config.debug) {
    }

    // Flush if batch size reached
    if (this._queue.length >= this._config.batchSize) {
      void this._flush()
    }
  }

  private async _flush(): Promise<void> {
    if (this._queue.length === 0) return

    const batch = this._queue.splice(0)

    if (this._config.debug) {
    }

    const body = JSON.stringify({ events: batch })

    // Prefer sendBeacon (non-blocking, works on page unload)
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' })
      const sent = navigator.sendBeacon(this._config.endpoint, blob)
      if (sent) return

      // sendBeacon can fail if payload too large — fall through to fetch
    }

    // Fallback: async fetch (fire-and-forget, never blocks UI)
    try {
      await fetch(this._config.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      })
    } catch {
      // Telemetry failures must never disrupt the app
      if (this._config.debug) {
      }
    }
  }

  private _trackPageLoad(): void {
    // Use Navigation Timing API (level 2 if available)
    const entries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[]
    if (entries.length > 0) {
      const nav = entries[0] as PerformanceNavigationTiming
      this._enqueue('performance', {
        metric: 'page_load',
        dnsMs: Math.round(nav.domainLookupEnd - nav.domainLookupStart),
        connectMs: Math.round(nav.connectEnd - nav.connectStart),
        ttfbMs: Math.round(nav.responseStart - nav.requestStart),
        domContentLoadedMs: Math.round(nav.domContentLoadedEventEnd - nav.startTime),
        loadCompleteMs: Math.round(nav.loadEventEnd - nav.startTime),
      })
    }
  }

  // Arrow functions to preserve `this` when used as event listeners
  private _onGlobalError = (event: ErrorEvent): void => {
    this._enqueue('unhandled_error', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error?.stack?.slice(0, 2000),
    })
  }

  private _onUnhandledRejection = (event: PromiseRejectionEvent): void => {
    const reason = event.reason
    this._enqueue('unhandled_rejection', {
      message: reason instanceof Error ? reason.message : String(reason),
      stack: reason instanceof Error ? reason.stack?.slice(0, 2000) : undefined,
    })
  }
}

// ─── Singleton export ────────────────────────────────────────────────────────

export const telemetry = new TelemetryService()
